<template>
  <div class="print-delivery-overview-component">
    <spinner-overlay v-if="loading" class="mt-5" />
    <print-container v-else :title="`Leveringsoversigt ${momentDate.format('DD-MM-YYYY')}`"
      :header_style="{ backgroundColor: '#b45f06', color: 'white' }">
      <template v-slot:header>
        <div class="h3">LEVERINGSOVERSIGT - {{ momentDate.format('dddd DD-MM-YYYY') }}</div>
      </template>
      <div class="container-fluid large-text">
        <div class="row">
          <div class="col-1"></div>
          <div class="col-11 pt-5">Total: {{ orderCount }}</div>
        </div>
        <div class="row text-primary">
          <div class="col-1"></div>
          <div class="col-11">Kold: {{ coldOrderCount }}</div>
        </div>
        <div class="row text-danger">
          <div class="col-1"></div>
          <div class="col-11">Varm: {{ warmOrderCount }}</div>
        </div>
        <div v-for="(trip, index) in allTrips" :key="index" class="print-avoid-break-inside" style="padding-top: 50px;">
          <div class="delimeter"></div>
          <div class="row">
            <div class="col-1"><i class="far fa-square"></i></div>
            <div class="col-1"></div>
            <div class="col-3">
              <a :href="`/order_management/orders?tab=delivery&_search[delivery_date_from]=${date}&_search[delivery_date_to]=${date}&_search[delivery_zone]=${trip.delivery_zone_id}`"
                class="print-link" target="_blank">
                <span v-if="trip.trip_start" class="single-line-block">Pak bil: {{ trip.trip_start
                  }}</span>
                <span v-else class="text-danger single-line-block">Ikke planlagt</span>
              </a>
            </div>
            <div class="col-3">
              <span v-if="trip.drive_start" class="single-line-block">Afgang: {{
                trip.drive_start }}</span>
            </div>
            <div class="col-2 single-line">
              <span class="single-line-block">{{ trip.driver_name }}</span>
            </div>
            <div class="col-2 single-line">
              <span class="single-line-block">{{ trip.vehicle_name }}</span>
            </div>
          </div>
          <div class="delimeter"></div>
          <template v-for="order in trip.orders" :key="order.id">
            <div v-if="order.isManualStop" class="row">
              <div class="col-1"><i class="far fa-square"></i></div>
              <div class="col-1"></div>
              <div class="col-5 single-line-block">{{ order.manualStopDescription }}</div>
              <div class="col-5 single-line-block">{{ order.manualStopAddress }}</div>
            </div>
            <div v-else class="row">
              <div class="col-1"><i class="far fa-square"></i></div>
              <div class="col-1 text-danger"><span v-if="order.delivery_type === 'warm'">Varm</span></div>
              <div class="col-8 single-line-block">{{ order.total_quantity }} x {{ order.productTitle }}</div>
              <div class="col-2"><a :href="order.url" class="print-link single-line-block" target="_blank">#{{ order.reference_code }}</a></div>
            </div>
            <div v-for="extra, index in order.order_products" class="row" :key="index">
              <div class="col-1"></div>
              <div class="col-1"></div>
              <div class="col-8 single-line-block">
                <span class="extra-product-line">{{ extra.quantity }} x {{ extra.product ?
                  extra.product.name : '' }}</span>
              </div>
            </div>
          </template>
          <div class="delimeter"></div>
        </div>
      </div>
    </print-container>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment/moment';
import { computed, ref } from 'vue';
import { Order } from '@/models/order';
import SpinnerOverlay from '@/components/SpinnerOverlay.vue';
import { useRouteDataStore } from '@/stores/route-data';
import PrintContainer from '@/print/PrintContainer.vue';

export default {
  components: { PrintContainer, SpinnerOverlay },
  props: {
    delivery_zones: {
      type: Array,
      required: true
    },
    vehicles: {
      type: Array,
      required: true
    },
    drivers: {
      type: Array,
      required: true
    },
  },
  setup(props) {
    const date = computed(() => {
      return new URLSearchParams(window.location.search).get('date');
    });
    const momentDate = computed(() => {
      moment.locale('da');
      return moment(date.value);
    });

    const loading = ref(true);
    const routeDataStore = useRouteDataStore();

    const orderCount = computed(() => {
      return allTrips.value.map(trip => trip.orders.length).reduce((a, b) => a + b, 0);
    });

    const coldOrderCount = computed(() => {
      return allTrips.value.map(trip => trip.orders.filter(order => order.delivery_type === 'cold').length).reduce((a, b) => a + b, 0);
    });

    const warmOrderCount = computed(() => {
      return allTrips.value.map(trip => trip.orders.filter(order => order.delivery_type === 'warm').length).reduce((a, b) => a + b, 0);
    });

    const allTrips = ref([]);

    const tripNames = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
    const getTrips = (orders) => {
      const ordersByTrips = { none: [], A: [], B: [], C: [], D: [], E: [], F: [], G: [] };
      for (let i = 0; i < orders.length; i++) {
        const order = orders[i];
        if (order.route_code) {
          ordersByTrips[order.route_code].push(order);
        } else {
          ordersByTrips.none.push(order);
        }
      }
      for (let i = 0; i < tripNames.length; i++) {
        ordersByTrips[tripNames[i]].sort((a, b) => {
          return a.tour_code - b.tour_code;
        });
      }
      ordersByTrips.none.sort((a, b) => {
        return a.deliveryIntervalStart.localeCompare(b.deliveryIntervalStart);
      });
      return ordersByTrips;
    };

    const loadOrders = async () => {
      // loop delivery_zones and get orders
      for (const deliveryZone of props.delivery_zones) {
        await axios
          .get(`/order_management/orders/orders_data?type=deliveries&date=${date.value}&delivery_zone=${deliveryZone.id}`)
          .then(response => {
            const allOrders = response.data.orders;
            const manualStops = response.data.route_data ? response.data.route_data.manual_stops ?? [] : [];
            for (let i = 0; i < manualStops.length; i++) {
              const stop = manualStops[i];
              allOrders.push({
                id: stop.id,
                isManualStop: true,
                delivery_zone_id: stop.delivery_zone_id,
                route_code: stop.route_code,
                tour_code: stop.tour_code,
                expected_delivery_time: stop.expected_delivery_time,
                manualStopAddress: stop.manualStopAddress,
                manualStopDescription: stop.manualStopDescription,
                vehicle_id: stop.vehicle_id,
                driver_id: stop.driver_id,
              });
            }

            const orders = allOrders.map((order) => {
              return Order.createReactive(order);
            });
            routeDataStore.setRouteData(response.data.route_data);

            //loop orders and add orders to allTrips
            const trips = getTrips(orders);
            for (const tripName of tripNames) {
              const orders = trips[tripName].filter(order => order.route_code === tripName);
              // console.log(deliveryZone.title, tripName, trips[tripName].length, orders.length);
              if (orders.length > 0) {
                const firstOrder = trips[tripName][0];
                allTrips.value.push({
                  trip_start: response.data.route_data === null ? null : firstOrder.tripStart,
                  drive_start: response.data.route_data === null ? null : firstOrder.driveStart,
                  driver_name: firstOrder.driver_id ? props.drivers.find(driver => driver.id === firstOrder.driver_id).name : '-',
                  vehicle_name: firstOrder.vehicle_id ? props.vehicles.find(vehicle => vehicle.id === firstOrder.vehicle_id).title : '-',
                  delivery_zone: deliveryZone.title,
                  delivery_zone_id: deliveryZone.id,
                  trip_name: tripName,
                  orders: orders,
                });
              }
            }
          });
      }
      allTrips.value.sort((a, b) => {
        if (a.drive_start === b.drive_start) {
          return 0;
        } else if (!a.drive_start) {
          return -1;
        } else if (!b.drive_start) {
          return 1;
        } else {
          return a.drive_start.localeCompare(b.drive_start);
        }
      });
      loading.value = false;
      console.log(allTrips.value);
      if (shouldOpenPrintDialog()) {
        setTimeout(window.print, 250);
      }
    };
    const shouldOpenPrintDialog = () => {
      const urlParams = new URLSearchParams(window.location.search);
      return !urlParams.has('no_print') || urlParams.get('no_print') !== 'true';
    }
    loadOrders();
    return {
      date,
      momentDate,
      allTrips,
      orderCount,
      coldOrderCount,
      warmOrderCount,
      loading,
    }
  }
}
</script>

<style lang="scss" scoped>
.print-delivery-overview-component {
  .delimeter {
    margin: 8px 0;
    border-top: 0.25mm solid #d0d0d0;
  }

  .text-transparent {
    color: transparent;
  }

  @media print {
    .print-link {
      color: inherit;
      text-decoration: none;
    }
  }

  @media screen {
    .print-link {
      text-decoration: underline;
    }
  }
}
</style>